import useRequest from "@/lib/request/useRequest";
import { object, number, string, boolean, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { RxType } from "@/types/enums";
import { FieldConfig } from "@/types/fieldConfig";
import { FertilizerProduct, FertilizerType } from "@/types/products";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import {
    Button,
    ModalContent,
    ModalBody,
    ModalHeader,
    Modal,
    ModalFooter,
} from "@heroui/react";
import SelectInput from "../inputs/SelectInput";
import SectionedAutoCompleteInput from "../inputs/SectionedAutoComplete";
import NumberInput from "../inputs/NumberInput";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

const schema = object().shape({
    target_rate_n: number().min(1).required(),
    fertilizer_product: string().required(),
    rx_type: string().required().oneOf(Object.values(RxType)),
    userIsSentinel: boolean().required().default(false),
    canary_offset: number()
        .required()
        .default(-30)
        .when("userIsSentinel", {
            is: true,
            then: (schema) => schema.max(0),
            otherwise: (schema) => schema.max(-30),
        }),
    reference_offset: number()
        .required()
        .when("userIsSentinel", {
            is: true,
            then: (schema) => schema.min(0),
            otherwise: (schema) => schema.min(30),
        })
        .when("rx_type", {
            is: RxType.ZONE,
            then: (schema) => schema.default(60),
            otherwise: (schema) => schema.default(30),
        }),
    max_speed_percentage: number()
        .default(100)
        .min(1)
        .max(100)
        .when("rx_type", {
            is: RxType.VRI,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.notRequired(),
        }),
});

type FormType = InferType<typeof schema>;

function BulkPlotRxFrom({
    fields,
    onFinish,
}: {
    fields: FieldConfig[];
    onFinish: () => void;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { t } = useTranslation("rx");
    const { formatUnit } = useI18n();

    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            target_rate_n: 30,
            rx_type: RxType.ZONE,
            canary_offset: -30,
            reference_offset: 60,
            max_speed_percentage: 100,
            fertilizer_product: "2",
            userIsSentinel: false,
        },
    });

    const [rxType, fertilizerProduct] = form.watch([
        "rx_type",
        "fertilizer_product",
    ]);

    const getFertilizers = useGetRequest<FertilizerProduct[]>({
        url: "/fertilizers/",
    });

    const fertilizerOptions = useMemo(() => {
        const coreProducts =
            getFertilizers.data
                ?.filter((fertilizer) => Boolean(fertilizer.core_product))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const favoriteFertilizers =
            getFertilizers.data
                ?.filter((fertilizer) => Boolean(fertilizer.is_favorite))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const dryFertilizers =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.Dry,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const liquidFertilizers =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.Liquid,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        return rxType === RxType.VRI
            ? [
                {
                    value: "favorite",
                    label: "Favorite Products",
                    options: favoriteFertilizers,
                },
                {
                    value: "core",
                    label: "Core Products",
                    options: coreProducts,
                },
                {
                    value: FertilizerType.Liquid,
                    label: "Liquid Fertilizer",
                    options: liquidFertilizers,
                },
            ]
            : [
                {
                    value: "favorite",
                    label: "Favorite Products",
                    options: favoriteFertilizers,
                },
                {
                    value: "core",
                    label: "Core Products",
                    options: coreProducts,
                },
                {
                    value: FertilizerType.Dry,
                    label: "Dry Fertilizer",
                    options: dryFertilizers,
                },
                {
                    value: FertilizerType.Liquid,
                    label: "Liquid Fertilizer",
                    options: liquidFertilizers,
                },
            ];
    }, [getFertilizers.data, rxType]);

    const selectedFertilizer = useMemo(() => {
        return getFertilizers.data?.find(
            (fertilizer) => fertilizer.id === Number(fertilizerProduct),
        );
    }, [getFertilizers.data, fertilizerProduct]);

    const fieldsWithoutIrrigation = useMemo(() => {
        return fields.filter(
            (field) => field.irrigation_system_configuration.count === 0,
        );
    }, [fields]);

    const createBulkPlotRx = useMutateRequest<{
        ok: { id: number; name: string }[];
        error: { id: number | null; error: string }[];
    }>({
        url: "/prescriptions/bulk_upsert/",
        method: "put",
        successMessage: "Plot Rxs created successfully.",
        errorMessage: "Error creating Plot Rxs.",
    });

    const handleSubmit = useCallback(
        (data: FormType) => {
            createBulkPlotRx.mutate(
                {
                    data: fields.map((field) => ({
                        name: `${field.name} Plot Rx ${dayjs().year()}`,
                        field: field.id,
                        target_rate_n: data.target_rate_n,
                        fertilizer_product: data.fertilizer_product,
                        rx_type: data.rx_type,
                        max_speed_percentage:
                            data.rx_type === RxType.VRI
                                ? data.max_speed_percentage
                                : undefined,
                        plot_placement: {
                            canary_offset: data.canary_offset,
                            reference_offset: data.reference_offset,
                        },
                    })),
                },
                {
                    onSuccess: () => {
                        onFinish();
                    },
                },
            );
        },
        [createBulkPlotRx, fields, onFinish],
    );

    return (
        <Modal isOpen size="xl" onClose={onFinish}>
            <ModalContent>
                <FormProvider {...form}>
                    <form>
                        <ModalHeader>Create Plot Rxs</ModalHeader>
                        <ModalBody>
                            <div className="flex flex-col gap-3">
                                <SectionedAutoCompleteInput
                                    name="fertilizer_product"
                                    label={t("fertilizer_product")}
                                    labelPlacement="outside"
                                    groups={fertilizerOptions}
                                />
                                <div className="flex flex-row gap-2">
                                    <NumberInput
                                        name="target_rate_n"
                                        label={t("n_rate")}
                                        labelPlacement="outside"
                                        endContent={
                                            <span className="text-sm">
                                                {formatUnit("lbs_ac", "kg_ha")}
                                            </span>
                                        }
                                    />
                                    <SelectInput
                                        name="rx_type"
                                        label={t("rx_type")}
                                        labelPlacement="outside"
                                        options={[
                                            {
                                                id: RxType.ZONE,
                                                description: "Zone",
                                            },
                                            {
                                                id: RxType.VRI,
                                                description: "VRI",
                                            },
                                        ]}
                                        onSelectionChange={(e) => {
                                            if (
                                                e.currentKey !== RxType.ZONE &&
                                                selectedFertilizer?.fertilizer_type ===
                                                FertilizerType.Dry
                                            ) {
                                                form.setValue(
                                                    "fertilizer_product",
                                                    "2",
                                                );
                                            }
                                        }}
                                    />
                                </div>
                                {form.getValues("rx_type") === RxType.VRI && (
                                    <NumberInput
                                        name="max_speed_percentage"
                                        label={t("max_speed")}
                                        labelPlacement="outside"
                                        endContent={
                                            <span className="text-sm">%</span>
                                        }
                                    />
                                )}
                                <div className="flex flex-row gap-2">
                                    <NumberInput
                                        name="canary_offset"
                                        label={t("canary_offset")}
                                        labelPlacement="outside"
                                        type="number"
                                        color="danger"
                                        endContent={
                                            <span className="text-sm">
                                                {formatUnit("lbs_ac", "kg_ha")}
                                            </span>
                                        }
                                    />
                                    <NumberInput
                                        name="reference_offset"
                                        label={t("reference_offset")}
                                        labelPlacement="outside"
                                        type="number"
                                        color="success"
                                        endContent={
                                            <span className="text-sm">
                                                {formatUnit("lbs_ac", "kg_ha")}
                                            </span>
                                        }
                                    />
                                </div>
                                {rxType === RxType.VRI &&
                                    fieldsWithoutIrrigation.length > 0 && (
                                        <div className="text-sm text-warning-500">
                                            {t("warning_no_irrigation_systems")}
                                            <span>: </span>
                                            {fieldsWithoutIrrigation
                                                .map((field) => field.name)
                                                .join(", ")}
                                        </div>
                                    )}
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <div className="flex flex-row gap-2 justify-end w-full">
                                <Button
                                    color="primary"
                                    onPress={() => {
                                        form.trigger();
                                        form.handleSubmit(handleSubmit)();
                                    }}
                                    isLoading={createBulkPlotRx.isPending}
                                    fullWidth
                                >
                                    Create Plot Rxs
                                </Button>
                            </div>
                        </ModalFooter>
                    </form>
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}

export default BulkPlotRxFrom;
